import { useMemo, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { styled, Box, Button, Typography } from '@mui/material';

import { FareSummary } from '@frontend/features/flight/components/FareSummary';

import { useGetItineraryById, useBookFareMutation } from '@frontend/features/flight/queries';

import { routes } from '../routes';

const FixedBox = styled(Box)`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2.5)};

  background-color: ${({ theme }) => theme.palette.paper.light};
  box-shadow: 0px -1px 16px 0px rgba(0, 0, 0, 0.25);

  box-sizing: border-box;
  padding: ${({ theme }) => theme.spacing(2.5, 2.5, 5)};
  position: fixed;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 178px;

  border-top-left-radius: 16px;
  border-top-right-radius: 16px;

  z-index: 2;
`;

type FixedFooterProps = {
  id: number;
  cost: string;
};
const FixedFooter = ({ id, cost }: FixedFooterProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    mutate: mutateBookFare,
    isLoading: isLoadingBookFare,
    isError: isBookFareError,
    data: fareBookData,
  } = useBookFareMutation();

  const handleOnClick = useCallback(() => {
    // TODO: actualizar cuando tengamos la data dinamica de la forma,
    // despues de refactorear toda la puta ui.
    mutateBookFare({
      id,
      passengers: [
        {
          id: '1',
          firstName: 'John',
          lastName: 'Doe',
          dob: '1980-01-01',
          gender: 'MALE',
          email: 'john.doe@example.com',
          phone: {
            deviceType: 'MOBILE',
            countryCallingCode: '1',
            number: '5551234567',
          },
          document: {
            documentType: 'PASSPORT',
            birthPlace: 'New York',
            issuedAt: 'New York',
            issued: '2015-01-01',
            number: 'A12345678',
            expires: '2025-01-01',
            country: 'US',
            nationality: 'US',
            holder: true,
          },
          paxType: 'ADT',
          requiredInformations: [
            {
              FieldName: 'NATL',
              FieldValue: 'USA',
            },
            {
              FieldName: 'FOID',
              FieldValue: '123456789',
            },
          ],
        },
      ],
      contact: {
        firstName: 'Jane',
        lastName: 'Smith',
        companyName: 'Example Company',
        purpose: 'STANDARD',
        phones: [
          {
            deviceType: 'LANDLINE',
            countryCallingCode: '1',
            number: '5559876543',
          },
        ],
        email: 'jane.smith@example.com',
        address: {
          lines: ['123 Main St'],
          postalCode: '10001',
          cityName: 'New York',
          countryCode: 'US',
        },
      },
    });
  }, [id]);

  useEffect(() => {
    if (isBookFareError) {
      console.error('error while booking flight');
    }

    if (!isLoadingBookFare && !isBookFareError && fareBookData?.data) {
      const path = routes.flights.payment.replace(':id', id.toString());
      navigate(path);
    }
  }, [isLoadingBookFare, isBookFareError, fareBookData, id]);

  return (
    <FixedBox>
      <Box width="100%" display="flex" justifyContent="space-between">
        <Typography color="text.primary" variant="h6" component="span">
          {t('flights.booking.total').toUpperCase()}
        </Typography>
        <Typography color="primary.dark" variant="h6" component="span">
          {cost}
        </Typography>
      </Box>
      <Button
        fullWidth
        variant="contained"
        size="large"
        disabled={isLoadingBookFare || isBookFareError}
        onClick={handleOnClick}
      >
        {t('flights.booking.continueBooking')}
      </Button>
    </FixedBox>
  );
};

export const FlightBooking = () => {
  const { id: bookingId } = useParams<{ id: string }>();

  const { t } = useTranslation();

  const queryParams = useMemo(() => (bookingId ? { id: parseInt(bookingId, 10) } : { id: 0 }), [bookingId]);

  const { data } = useGetItineraryById(queryParams.id);

  const price = parseInt(data?.data.price || '');
  const currency = data?.data.currency;

  return (
    <Box display="flex" flexDirection="column" rowGap={2}>
      <Box
        sx={{
          marginBottom: (theme) => theme.spacing(19),
        }}
      >
        <FareSummary title={t('flights.booking.purchaseDetails')} fareId={queryParams.id} />
      </Box>
      <FixedFooter id={queryParams.id} cost={`${currency} ${price}`} />
    </Box>
  );
};
